import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CompanyVendor,
  ExemptVendor,
  License,
  Vendor
} from 'src/app/classes/models';
import { VendorTrade } from 'src/app/classes/models/vendor-trade';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-client-my-vendor-trades-and-licenses',
  templateUrl: './client-my-vendor-trades-and-licenses.component.html',
  styleUrls: ['./client-my-vendor-trades-and-licenses.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendorTradesAndLicensesComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private CompanyVendorService: CompanyVendorService,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private ComplianceService: ComplianceService
  ) {}

  CompanyId!: number;
  VendorId!: number;
  Vendor!: Vendor | ExemptVendor;
  VendorTrades!: VendorTrade[];
  vendorTradesWithNonArchivedLicenses: VendorTrade[] = [];
  vendorTradesWithArchivedLicenses: VendorTrade[] = [];
  showArchived: boolean = false;

  viewLicense(license: License): void {
    const { licenseFile, LicenseFile } = license;

    console.log(this.CompanyId);

    if (LicenseFile) {
      this.CompanyVendorService.getCompanyVendorLicenseFileS3PresignedUrl({
        CompanyId: this.CompanyId,
        VendorId: this.VendorId,
        LicenseId: license.id!
      }).subscribe({
        next: (s3Response) => {
          window.open(s3Response.presignedUrl, '_blank');
        }
      });
    } else if (licenseFile) {
      let base64Data = licenseFile;
      let mimeType = 'application/pdf';

      if (licenseFile.startsWith('data:')) {
        const match = licenseFile.match(/^data:([^;]+);base64,(.*)$/);
        if (match) {
          mimeType = match[1];
          base64Data = match[2];
        }
      } else if (licenseFile.includes('data:application/pdf;base64,')) {
        base64Data = licenseFile.split(',')[1];
      }

      const arrayBuffer =
        this.UtilityService.convertBase64StringToArrayBuffer(base64Data);
      const unit8Array = new Uint8Array(arrayBuffer);
      const blob = new Blob([unit8Array], { type: mimeType });
      const fileUrl = URL.createObjectURL(blob);

      window.open(fileUrl, '_blank');
    } else {
      throw this.ToastService.addErrorToast(
        'Error',
        'No license file was found.',
        { returnError: true }
      );
    }
  }

  filterVendorTradesOnLicenseStatus(
    statuses: string[],
    vendorTrades: VendorTrade[]
  ): VendorTrade[] {
    const deepCopyVendorTrades: VendorTrade[] = JSON.parse(
      JSON.stringify(vendorTrades)
    );
    return deepCopyVendorTrades.map((trade) => {
      if (
        trade.PrimaryTrade?.PrimaryTradeLicenseRequirements &&
        trade.PrimaryTrade.PrimaryTradeLicenseRequirements.length > 0
      ) {
        const filteredPTR =
          trade.PrimaryTrade.PrimaryTradeLicenseRequirements.map((req) => {
            if (req.Licenses && req.Licenses.length > 0) {
              const filteredLicenses = req.Licenses.filter((l) =>
                statuses.includes(l.status!)
              );
              req.Licenses = filteredLicenses;
            }
            return req;
          });
        trade.PrimaryTrade.PrimaryTradeLicenseRequirements = filteredPTR;
      }

      return trade;
    });
  }

  ngOnInit(): void {
    const paramMap = this.route.snapshot.paramMap;
    const CompanyId = Number(paramMap.get('CompanyId')) || null;
    const VendorId = Number(paramMap.get('VendorId')) || null;

    if (!CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing client information',
        { returnError: true }
      );
    }

    if (!VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor information',
        { returnError: true }
      );
    }

    this.CompanyId = CompanyId;
    this.VendorId = VendorId;

    this.ComplianceService.getClientVendorSharedStates(
      CompanyId,
      VendorId
    ).subscribe({
      next: (ClientVendorSharedStates) => {
        this.CompanyVendorService.getCompanyVendor(
          this.UtilityService.stringifyObjectsInQueryObject({
            CompanyId,
            VendorId,
            attributes: ['legalName'],
            Vendor: {
              VendorTradeStates: {
                SubNationalDivision: {}
              },
              VendorTrade: {
                PrimaryTrade: {
                  PrimaryTradeLicenseRequirement: {
                    options: { required: false },
                    state: { $in: ClientVendorSharedStates },
                    License: {
                      attributes: [
                        'id',
                        'status',
                        'licenseFile',
                        'licenseNumber',
                        'licenseState',
                        'expirationDate',
                        'updatedAt',
                        'VendorId'
                      ],
                      status: {
                        $in: ['Active', 'Revoked', 'Lapsed', 'Archived']
                      },
                      VendorId,
                      LicenseFile: {}
                    }
                  }
                }
              }
            }
          })
        ).subscribe({
          next: (companyVendor: CompanyVendor) => {
            const { Vendor } = companyVendor;

            if (Vendor) {
              this.Vendor = Vendor;
            }

            if (this.Vendor) {
              const { VendorTrades } = this.Vendor;

              if (VendorTrades && VendorTrades.length > 0) {
                this.VendorTrades = VendorTrades;
                this.vendorTradesWithNonArchivedLicenses =
                  this.filterVendorTradesOnLicenseStatus(
                    ['Active', 'Revoked', 'Lapsed'],
                    VendorTrades
                  );
                this.vendorTradesWithArchivedLicenses =
                  this.filterVendorTradesOnLicenseStatus(
                    ['Archived'],
                    VendorTrades
                  );
                this.vendorTradesWithArchivedLicenses =
                  this.vendorTradesWithArchivedLicenses.map((trade) => {
                    const filteredRequirements =
                      trade.PrimaryTrade?.PrimaryTradeLicenseRequirements?.filter(
                        (req) => req.Licenses!.length > 0
                      );
                    trade.PrimaryTrade!.PrimaryTradeLicenseRequirements =
                      filteredRequirements;
                    return trade;
                  });
                this.vendorTradesWithArchivedLicenses =
                  this.vendorTradesWithArchivedLicenses.filter(
                    (trade) =>
                      trade.PrimaryTrade!.PrimaryTradeLicenseRequirements!
                        .length > 0
                  );
              }
            } else {
              throw this.ToastService.addErrorToast(
                'Error',
                'No Vendor information found!',
                { returnError: true }
              );
            }
            this._cdr.markForCheck();
          }
        });
      }
    });
  }
}
